import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth, canSeeRestrictedCultural } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { DataField, RestrictedNotice, SectionTitle } from "../components/DataField";
import { PermissionStatusBadge, ClimateRiskBadge, VisibilityBadge, CulturalApprovalBadge } from "../components/Badges";
import {
  Sprout, MapPinned, Dna, Tent, Sun, Recycle, Sparkles, Upload, FileText, Image as ImageIcon, Mic2, ShieldCheck,
  RefreshCw, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";

const markerIcon = new L.DivIcon({
  className: "",
  iconSize: [14, 14],
  iconAnchor: [7, 7],
  html: `<div style="width:14px;height:14px;border-radius:50%;background:#8B5A2B;border:2px solid #FDFBF7;box-shadow:0 0 0 1px #8B5A2B33"></div>`,
});

const CULTURAL_RESTRICTED = ["elder_observation_notes", "cultural_name", "traditional_uses", "restricted_knowledge_notes", "cultural_governance_notes"];

export default function CollectionProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const { data: c, isLoading, refetch } = useQuery({
    queryKey: ["collection", id],
    queryFn: async () => (await api.get(`/collections/${id}`)).data,
  });

  if (isLoading || !c) {
    return <div className="text-[12px] tracking-[0.2em] uppercase text-[#8A857D]">Loading…</div>;
  }

  const showRestricted = canSeeRestrictedCultural(user);
  const eco = c.ecological || {};
  const gen = c.genetic || {};
  const cul = c.cultural || {};
  const cli = c.climate || {};
  const res = c.restoration || {};
  const ai = c.ai_insights || {};

  const regenerate = async () => {
    try {
      await api.post(`/collections/${id}/insights/regenerate`);
      toast.success("Provenance Intelligence refreshed.");
      qc.invalidateQueries({ queryKey: ["collection", id] });
    } catch {
      toast.error("Could not regenerate insights.");
    }
  };

  const uploadFile = async (fileType, file, visibility = "internal") => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("collection_id", id);
      fd.append("file_type", fileType);
      fd.append("visibility_level", visibility);
      fd.append("file", file);
      await api.post("/files/upload", fd);
      toast.success(`${fileType.replace("_", " ")} uploaded.`);
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const files = c.files || [];
  const photos = files.filter((f) => f.file_type === "photo");
  const audio = files.filter((f) => f.file_type === "audio");
  const docs = files.filter((f) => f.file_type === "permission_doc");

  return (
    <div className="space-y-8" data-testid="collection-profile-page">
      <Link to="/collections" className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase font-semibold text-[#8B5A2B] hover:underline" data-testid="back-to-list">
        <ArrowLeft className="w-3.5 h-3.5" /> All collections
      </Link>

      {/* ----- Summary header ----- */}
      <Card className="p-7 lg:p-9 border-[#E6E2D8] shadow-sm bg-white">
        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-8">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono-data text-[11px] text-[#8A857D] tracking-tight">{c.collection_id}</span>
              <VisibilityBadge level={c.visibility_level} />
              <PermissionStatusBadge status={cul.cultural_permission_status} />
              {c._redacted && <CulturalApprovalBadge />}
            </div>
            <h1 className="font-display text-4xl tracking-tight font-bold text-[#2C2A28] mt-3">
              <span className="font-serif-display italic">{c.species_scientific_name || "Unidentified species"}</span>
            </h1>
            <p className="text-[14px] text-[#5C5854] mt-1">{c.species_common_name || ""}</p>

            <div className="grid sm:grid-cols-3 gap-4 mt-7">
              <DataField label="Collected" value={c.date_collected} testid="sum-date" />
              <DataField label="Collector" value={c.collector_name} testid="sum-collector" />
              <DataField label="Project" value={c.project_name} testid="sum-project" />
              <DataField label="Family" value={c.plant_family} testid="sum-family" />
              <DataField label="Quantity" value={c.seed_quantity ? `${c.seed_quantity} ${c.seed_unit || ""}` : null} testid="sum-qty" />
              <DataField label="Storage" value={c.storage_location} testid="sum-storage" />
            </div>
          </div>

          <div className="rounded-md border border-[#E6E2D8] overflow-hidden h-[260px]">
            {(eco.latitude && eco.longitude) ? (
              <MapContainer center={[eco.latitude, eco.longitude]} zoom={6} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
                <TileLayer attribution='© OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[eco.latitude, eco.longitude]} icon={markerIcon}>
                  <Popup>{c.collection_id}<br/>{c.species_scientific_name}</Popup>
                </Marker>
              </MapContainer>
            ) : (
              <div className="h-full w-full sk-grain flex items-center justify-center text-[12px] text-[#8A857D]">No GPS coordinates recorded</div>
            )}
          </div>
        </div>
      </Card>

      {/* ----- AI Insights ----- */}
      <Card className="sk-ai-surface p-7 lg:p-9 rounded-md shadow-sm" data-testid="ai-insights-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#FFF1E5] border border-[#D08C38] flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-[#8B5A2B]" strokeWidth={1.6} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-display text-xl font-semibold tracking-tight text-[#2C2A28]">Provenance Intelligence</h2>
                <span className="text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-sm bg-[#FFF7E8] border border-[#D08C38] text-[#9B6716] font-semibold">Draft intelligence</span>
              </div>
              <p className="text-[12.5px] text-[#5C5854] mt-1">Generated by rule-based heuristics. Always verify with cultural and scientific authority.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854]">Confidence</div>
              <div className="font-mono-data text-2xl text-[#2C2A28]">{ai.confidence_score ?? "—"}%</div>
            </div>
            <Button variant="outline" onClick={regenerate} className="gap-2 border-[#E6E2D8] text-[#5C5854] hover:bg-[#F4F1EA]" data-testid="regenerate-ai">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
          </div>
        </div>

        <div className="mt-6 grid lg:grid-cols-2 gap-x-8 gap-y-5">
          <Insight label="Provenance summary" text={ai.provenance_summary} />
          <Insight label="Ecological suitability" text={ai.ecological_summary} />
          <Insight label="Genetic risk" text={ai.genetic_summary} />
          <Insight label="Cultural governance" text={ai.cultural_governance_summary} />
          <Insight label="Climate adaptation" text={ai.climate_summary} />
          <Insight label="Restoration recommendations" text={ai.restoration_recommendations} />
        </div>

        {(ai.risk_warnings?.length || ai.missing_data_alerts?.length) > 0 && (
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            {ai.risk_warnings?.length > 0 && (
              <div className="rounded-md border border-[#C47F4B] bg-[#FFF1E5] p-4">
                <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#B45B3E]">Risk warnings</div>
                <ul className="mt-2 space-y-1.5 list-disc list-inside text-[12.5px] text-[#B45B3E]">
                  {ai.risk_warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
            {ai.missing_data_alerts?.length > 0 && (
              <div className="rounded-md border border-[#E6E2D8] bg-[#F4F1EA] p-4">
                <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5854]">Missing data</div>
                <ul className="mt-2 space-y-1.5 list-disc list-inside text-[12.5px] text-[#5C5854]">
                  {ai.missing_data_alerts.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ----- Layers tabs ----- */}
      <Tabs defaultValue="ecological" className="w-full">
        <TabsList className="bg-transparent border-b border-[#E6E2D8] w-full justify-start rounded-none h-auto p-0 gap-0">
          {[
            { v: "ecological", label: "Ecological", icon: MapPinned },
            { v: "genetic", label: "Genetic", icon: Dna },
            { v: "cultural", label: "Cultural", icon: Tent },
            { v: "climate", label: "Climate", icon: Sun },
            { v: "restoration", label: "Restoration", icon: Recycle },
            { v: "files", label: "Files", icon: FileText },
          ].map((t) => (
            <TabsTrigger
              key={t.v}
              value={t.v}
              data-testid={`tab-${t.v}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#8B5A2B] data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[#5C5854] data-[state=active]:text-[#2C2A28] px-5 py-3 gap-2"
            >
              <t.icon className="w-3.5 h-3.5" strokeWidth={1.8} /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="ecological" className="mt-7">
          <Card className="p-7 border-[#E6E2D8] shadow-sm bg-white">
            <SectionTitle icon={MapPinned} title="Ecological layer" subtitle="Country, habitat, and the conditions that shaped this seed." />
            <div className="grid md:grid-cols-3 gap-x-8 gap-y-5">
              <DataField label="Latitude" value={eco.latitude} mono />
              <DataField label="Longitude" value={eco.longitude} mono />
              <DataField label="Elevation" value={eco.elevation ? `${eco.elevation} m` : null} mono />
              <DataField label="Soil type" value={eco.soil_type} />
              <DataField label="Rainfall zone" value={eco.rainfall_zone} />
              <DataField label="Vegetation community" value={eco.vegetation_community} />
              <DataField label="Associated species" value={eco.associated_species} />
              <DataField label="Landform" value={eco.landform} />
              <DataField label="Slope · Aspect" value={[eco.slope, eco.aspect].filter(Boolean).join(" · ") || null} />
              <DataField label="Fire history" value={eco.fire_history} />
              <DataField label="Habitat condition" value={eco.habitat_condition_rating} />
              <DataField label="Recent rainfall" value={eco.recent_rainfall_notes} />
              <DataField label="Soil notes" value={eco.soil_notes} />
              <DataField label="Disturbance notes" value={eco.disturbance_notes} />
              <DataField label="Pest / disease" value={eco.pest_disease_notes} />
              <DataField label="Ecological notes" value={eco.ecological_notes} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="genetic" className="mt-7">
          <Card className="p-7 border-[#E6E2D8] shadow-sm bg-white">
            <SectionTitle icon={Dna} title="Genetic layer" subtitle="Sampling, population health and provenance." />
            <div className="grid md:grid-cols-3 gap-x-8 gap-y-5">
              <DataField label="DNA sample taken" value={gen.dna_sample_taken ? "Yes" : "No"} />
              <DataField label="DNA sample ID" value={gen.dna_sample_id} mono />
              <DataField label="Tissue type" value={gen.tissue_sample_type} />
              <DataField label="Parent plants sampled" value={gen.parent_plants_sampled} mono />
              <DataField label="Parent plant spacing" value={gen.parent_plant_spacing} />
              <DataField label="Population estimate" value={gen.population_size_estimate} mono />
              <DataField label="Population health" value={gen.population_health_rating} />
              <DataField label="Provenance zone" value={gen.provenance_zone} />
              <DataField label="Genetic testing lab" value={gen.genetic_testing_lab} />
              <DataField label="Testing status" value={gen.genetic_testing_status} />
              <DataField label="Diversity score" value={gen.genetic_diversity_score} mono />
              <DataField label="Inbreeding risk" value={gen.inbreeding_risk} />
              <DataField label="Hybridisation risk" value={gen.hybridisation_risk} />
              <DataField label="Flowering variation" value={gen.flowering_variation} />
              <DataField label="Fruiting variation" value={gen.fruiting_variation} />
              <DataField label="Diversity notes" value={gen.genetic_diversity_notes} />
              <DataField label="Genetic notes" value={gen.genetic_notes} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="cultural" className="mt-7">
          <Card className="p-7 border-[#E6E2D8] shadow-sm bg-white">
            <SectionTitle
              icon={Tent}
              title="Cultural layer"
              subtitle="Traditional Owner authority, permissions and knowledge."
              accent={!showRestricted ? <CulturalApprovalBadge /> : null}
            />
            {!showRestricted && (
              <div className="mb-6"><RestrictedNotice>Restricted fields are visible only to the Traditional Owner Admin for this group and System Admins.</RestrictedNotice></div>
            )}
            <div className="grid md:grid-cols-3 gap-x-8 gap-y-5">
              <DataField label="Traditional Owner group" value={cul.traditional_owner_group} />
              <DataField label="Authority contact" value={cul.cultural_authority_contact} />
              <DataField label="Permission status" value={cul.cultural_permission_status} />
              <DataField label="Permission date" value={cul.permission_date} />
              <DataField label="Sensitivity rating" value={cul.cultural_sensitivity_rating} />
              <DataField label="Story permissions" value={cul.story_permissions} />
              <DataField label="Public sharing" value={cul.public_sharing_allowed ? "Allowed" : "Not allowed"} />
              <DataField label="Partner sharing" value={cul.partner_sharing_allowed ? "Allowed" : "Not allowed"} />
              <DataField label="AI analysis allowed" value={cul.ai_analysis_allowed ? "Allowed" : "Not allowed"} />

              {/* Restricted fields */}
              {CULTURAL_RESTRICTED.map((k) => (
                <DataField
                  key={k}
                  label={k.replace(/_/g, " ")}
                  value={cul[k]}
                  restricted={!showRestricted}
                  testid={`restricted-${k}`}
                />
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="climate" className="mt-7">
          <Card className="p-7 border-[#E6E2D8] shadow-sm bg-white">
            <SectionTitle icon={Sun} title="Climate layer" subtitle="Current exposure and future suitability." accent={<ClimateRiskBadge score={cli.climate_risk_score} />} />
            <div className="grid md:grid-cols-3 gap-x-8 gap-y-5">
              <DataField label="Climate zone" value={cli.current_climate_zone} />
              <DataField label="Mean rainfall" value={cli.mean_annual_rainfall ? `${cli.mean_annual_rainfall} mm` : null} mono />
              <DataField label="Mean temperature" value={cli.mean_annual_temperature ? `${cli.mean_annual_temperature} °C` : null} mono />
              <DataField label="Drought exposure" value={cli.drought_exposure} />
              <DataField label="Flood exposure" value={cli.flood_exposure} />
              <DataField label="Cyclone exposure" value={cli.cyclone_exposure} />
              <DataField label="Fire exposure" value={cli.fire_exposure} />
              <DataField label="Future suitability" value={cli.future_climate_suitability_rating} />
              <DataField label="Projection year" value={cli.climate_projection_year} mono />
              <DataField label="Future rainfall" value={cli.future_rainfall_projection} />
              <DataField label="Future temperature" value={cli.future_temperature_projection} />
              <DataField label="Climate risk score" value={cli.climate_risk_score} mono />
              <DataField label="Assisted migration" value={cli.assisted_migration_suitability} />
              <DataField label="Resilience notes" value={cli.climate_resilience_notes} />
              <DataField label="Future collection zones" value={cli.recommended_future_collection_zones} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="restoration" className="mt-7">
          <Card className="p-7 border-[#E6E2D8] shadow-sm bg-white">
            <SectionTitle icon={Recycle} title="Restoration use" subtitle="How, where and with whom this seed may be used." />
            <div className="grid md:grid-cols-3 gap-x-8 gap-y-5">
              <DataField label="Recommended use" value={res.recommended_restoration_use} />
              <DataField label="Suitable project types" value={res.suitable_project_types} />
              <DataField label="Carbon project" value={res.carbon_project_suitability} />
              <DataField label="Mine rehabilitation" value={res.mine_rehabilitation_suitability} />
              <DataField label="Biodiversity repair" value={res.biodiversity_repair_suitability} />
              <DataField label="Storage requirements" value={res.storage_requirements} />
              <DataField label="Viability % " value={res.viability_percentage} mono />
              <DataField label="Viability date" value={res.viability_testing_date} />
              <DataField label="Planting zones" value={res.recommended_planting_zones} />
              <DataField label="Commercial use" value={res.commercial_use_permission} />
              <DataField label="Nursery propagation" value={res.nursery_propagation_notes} />
              <DataField label="Seed treatment" value={res.seed_treatment_notes} />
              <DataField label="Germination notes" value={res.germination_notes} />
              <DataField label="Restrictions on use" value={res.restrictions_on_use} />
              <DataField label="Benefit sharing" value={res.benefit_sharing_requirements} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="files" className="mt-7">
          <Card className="p-7 border-[#E6E2D8] shadow-sm bg-white space-y-7">
            <SectionTitle icon={FileText} title="Files & evidence" subtitle="Photos, audio observations and permission documents." />

            <FileSection
              title="Photos"
              icon={ImageIcon}
              files={photos}
              accept="image/*"
              fileType="photo"
              onUpload={uploadFile}
              uploading={uploading}
              testidPrefix="photo"
            />
            <FileSection
              title="Audio observations"
              icon={Mic2}
              files={audio}
              accept="audio/*"
              fileType="audio"
              onUpload={uploadFile}
              uploading={uploading}
              testidPrefix="audio"
            />
            <FileSection
              title="Permission documents"
              icon={ShieldCheck}
              files={docs}
              accept=".pdf,.doc,.docx,image/*"
              fileType="permission_doc"
              onUpload={uploadFile}
              uploading={uploading}
              testidPrefix="doc"
            />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Insight({ label, text }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#8B5A2B]">{label}</div>
      <p className="text-[13.5px] leading-relaxed text-[#2C2A28] mt-1.5">{text || <span className="text-[#8A857D]">Not generated yet.</span>}</p>
    </div>
  );
}

function FileSection({ title, icon: Icon, files, accept, fileType, onUpload, uploading, testidPrefix }) {
  const [visibility, setVisibility] = useState("internal");
  const inputId = `file-input-${fileType}`;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#8B5A2B]" strokeWidth={1.8} />
        <h3 className="font-display text-[15px] font-semibold text-[#2C2A28]">{title}</h3>
        <span className="ml-auto text-[11px] text-[#8A857D]">{files.length} file{files.length === 1 ? "" : "s"}</span>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <input
          id={inputId}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => onUpload(fileType, e.target.files?.[0], visibility)}
          data-testid={`${testidPrefix}-upload-input`}
        />
        <label htmlFor={inputId} className="cursor-pointer inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase font-semibold border border-[#E6E2D8] bg-white px-4 py-2 rounded-md hover:bg-[#F4F1EA] text-[#2C2A28]">
          <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload"}
        </label>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-[#8A857D] tracking-[0.18em] uppercase">Visibility:</span>
          {["internal", "public", "restricted"].map((v) => (
            <button
              key={v}
              onClick={() => setVisibility(v)}
              className={`px-2 py-1 rounded-sm border ${visibility === v ? "border-[#8B5A2B] bg-[#FFF7E8] text-[#8B5A2B]" : "border-[#E6E2D8] text-[#5C5854] hover:bg-[#F4F1EA]"}`}
              data-testid={`${testidPrefix}-vis-${v}`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {files.length === 0 ? (
        <div className="text-[12.5px] text-[#8A857D] italic">No {title.toLowerCase()} uploaded yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {files.map((f) => (
            <FileTile key={f.id} file={f} fileType={fileType} testid={`${testidPrefix}-tile-${f.id}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function FileTile({ file, fileType, testid }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const url = `${BACKEND}/api/files/${file.id}/download`;
  return (
    <div className="border border-[#E6E2D8] rounded-md p-3 bg-white hover:shadow-md transition-all" data-testid={testid}>
      {fileType === "photo" ? (
        <a href={url} target="_blank" rel="noreferrer">
          <img src={url} alt={file.file_name} className="w-full h-32 object-cover rounded-sm bg-[#F4F1EA]" />
        </a>
      ) : fileType === "audio" ? (
        <audio controls src={url} className="w-full" />
      ) : (
        <a href={url} target="_blank" rel="noreferrer" className="block text-[13px] font-medium text-[#8B5A2B] underline-offset-4 hover:underline">
          <FileText className="w-6 h-6" />
          {file.file_name}
        </a>
      )}
      <div className="mt-2 flex items-center justify-between">
        <div className="text-[11px] text-[#5C5854] truncate">{file.file_name}</div>
        <VisibilityBadge level={file.visibility_level} />
      </div>
    </div>
  );
}
